import type { CmtsRouteDefinition } from "./cmtsRouteTypes.js";
import { CMTS_ROLE_CODES } from "../auth/cmtsRoleCodes.js";

export const cmtsRouteManifest: readonly CmtsRouteDefinition[] = [
  {
    id: "auth.cmts.login",
    path: "/cmts/login",
    title: "Login",
    module: "Authentication",
    feature: "auth",
    permission: "PUBLIC",
    componentExport: "CmtsLoginContainer",
    showInMenu: false,
    requiresTenantScope: false,
  },
  {
    id: "tasks",
    path: "/cmts/tasks",
    title: "My Tasks",
    module: "My Work",
    feature: "tasks",
    permission: "TASK.READ",
    componentExport: "CmtsTasksContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "my-work",
    menuGroup: "My Work",
    menuOrder: 2,
    menuDescription: "Assigned workflow tasks",
    personaLandingFor: ["FUNCTIONAL_EXPERT"],
    operationSpine: { "domain": "TASKS", "primaryActions": ["complete-task", "reassign-task"], "requiredSeedEntities": ["tasks", "complaints", "users"] },
  },
  {
    id: "tasks.cmts.tasks.my",
    path: "/cmts/tasks/my",
    title: "My Tasks",
    module: "My Work",
    feature: "tasks",
    permission: "TASK.READ",
    componentExport: "CmtsTasksContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "TASKS", "primaryActions": ["complete-task", "reassign-task"], "requiredSeedEntities": ["tasks", "complaints", "users"] },
  },
  {
    id: "messages",
    path: "/cmts/messages",
    title: "Messages",
    module: "My Work",
    feature: "messages",
    permission: "MESSAGE.READ",
    componentExport: "CmtsMessagesContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "my-work",
    menuGroup: "My Work",
    menuOrder: 4,
    menuDescription: "Complaint communication",
    operationSpine: { "domain": "MESSAGES", "primaryActions": ["reply-message", "archive-message"], "requiredSeedEntities": ["messages", "complaints", "users"] },
  },
  {
    id: "reference-data",
    path: "/cmts/admin/reference-data",
    title: "Reference Data",
    module: "Administration",
    feature: "references",
    permission: "MASTER_DEPENDENCY.READ",
    componentExport: "CmtsReferenceAdminContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_REFERENCE", "primaryActions": ["open-reference-data"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32,
    menuDescription: "Reference administration",
  },
  {
    id: "dashboard.cmts",
    path: "/cmts",
    title: "CMTS Home",
    module: "Dashboards",
    feature: "dashboard",
    permission: "DASHBOARD.READ",
    componentExport: "CmtsDashboardsContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "DASHBOARD", "primaryActions": ["open-drilldown"], "requiredSeedEntities": ["dashboardSignals", "complaints", "tasks"] },
  },
  {
    id: "workspace",
    path: "/cmts/my-workspace",
    title: "My Workspace",
    module: "My Work",
    feature: "workspace",
    permission: "DASHBOARD.READ",
    componentExport: "CmtsMyWorkspaceContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "MY_WORKSPACE", "primaryActions": ["open-workspace"], "requiredSeedEntities": ["tasks", "complaints", "notifications"] },
    menuGroupId: "my-work",
    menuGroup: "My Work",
    menuOrder: 1,
    menuDescription: "Role-based work queue",
    personaLandingFor: ["PERMISSION:DASHBOARD.READ"],
  },
  {
    id: "my-work.cmts.my-work",
    path: "/cmts/my-work",
    title: "My Work",
    module: "My Work",
    feature: "my-work",
    permission: "DASHBOARD.READ",
    componentExport: "CmtsMyWorkspaceContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "MY_WORKSPACE", "primaryActions": ["open-workspace"], "requiredSeedEntities": ["tasks", "complaints", "notifications"] },
  },
  {
    id: "my-work.cmts.my-work.dashboard",
    path: "/cmts/my-work/dashboard",
    title: "My Work Dashboard",
    module: "My Work",
    feature: "my-work",
    permission: "DASHBOARD.READ",
    componentExport: "CmtsMyWorkspaceContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "MY_WORKSPACE", "primaryActions": ["open-dashboard"], "requiredSeedEntities": ["dashboardKpis", "tasks", "complaints"] },
  },
  {
    id: "tasks.cmts.my-work.tasks",
    path: "/cmts/my-work/tasks",
    title: "My Tasks",
    module: "My Work",
    feature: "tasks",
    permission: "TASK.READ",
    componentExport: "CmtsTasksContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "TASKS", "primaryActions": ["complete-task", "reassign-task"], "requiredSeedEntities": ["tasks", "complaints", "users"] },
  },
  {
    id: "workflow-inbox.cmts.my-work.assigned",
    path: "/cmts/my-work/assigned",
    title: "My Assigned Work",
    module: "My Work",
    feature: "workflow-inbox",
    permission: "TASK.READ",
    componentExport: "CmtsTasksContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "TASKS", "primaryActions": ["open-assigned-work"], "requiredSeedEntities": ["tasks", "complaints", "users"] },
  },
  {
    id: "workflow-inbox.cmts.my-work.approvals",
    path: "/cmts/my-work/approvals",
    title: "My Approvals",
    module: "My Work",
    feature: "workflow-inbox",
    permission: "TASK.READ",
    componentExport: "CmtsTasksContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "TASKS", "primaryActions": ["open-approval-work"], "requiredSeedEntities": ["tasks", "workflowTransitions", "users"] },
  },
  {
    id: "workflow-inbox.cmts.my-work.overdue",
    path: "/cmts/my-work/overdue",
    title: "My Overdue Work",
    module: "My Work",
    feature: "workflow-inbox",
    permission: "TASK.READ",
    componentExport: "CmtsTasksContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "TASKS", "primaryActions": ["open-overdue-work"], "requiredSeedEntities": ["tasks", "complaintSlaStates", "calendarEvents"] },
  },
  {
    id: "notifications.cmts.my-work.notifications",
    path: "/cmts/my-work/notifications",
    title: "My Notifications",
    module: "My Work",
    feature: "notifications",
    permission: "NOTIFICATION.READ",
    componentExport: "CmtsNotificationsContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "NOTIFICATIONS", "primaryActions": ["mark-notification-read", "acknowledge-notification"], "requiredSeedEntities": ["notifications", "complaints", "users"] },
  },
  {
    id: "messages.cmts.my-work.messages",
    path: "/cmts/my-work/messages",
    title: "My Messages",
    module: "My Work",
    feature: "messages",
    permission: "MESSAGE.READ",
    componentExport: "CmtsMessagesContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "MESSAGES", "primaryActions": ["reply-message", "archive-message"], "requiredSeedEntities": ["messages", "complaints", "users"] },
  },
  {
    id: "complaint-register",
    path: "/cmts/complaints",
    title: "Complaint Register",
    module: "Complaint Management",
    feature: "complaints",
    permission: "COMPLAINT.READ",
    componentExport: "CmtsComplaintRegisterContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "complaint-management",
    menuGroup: "Complaint Management",
    menuOrder: 5,
    menuDescription: "Search and operate complaints",
    operationSpine: { "domain": "COMPLAINTS", "primaryActions": ["create-complaint", "update-complaint", "assign-complaint"], "requiredSeedEntities": ["complaints", "workflowProfiles", "users"] },
  },
  {
    id: "complaints.cmts.complaints.register",
    path: "/cmts/complaints/register",
    title: "Complaint Register",
    module: "Complaint Management",
    feature: "complaints",
    permission: "COMPLAINT.READ",
    componentExport: "CmtsComplaintRegisterContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "COMPLAINTS", "primaryActions": ["create-complaint", "update-complaint", "assign-complaint"], "requiredSeedEntities": ["complaints", "workflowProfiles", "users"] },
  },
  {
    id: "create-complaint",
    path: "/cmts/complaints/create",
    title: "Create Complaint",
    module: "Complaint Management",
    feature: "complaints",
    permission: "COMPLAINT.CREATE",
    componentExport: "CmtsComplaintCreateContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "complaint-management",
    menuGroup: "Complaint Management",
    menuOrder: 6,
    menuDescription: "New complaint intake",
    operationSpine: { "domain": "COMPLAINTS", "primaryActions": ["create-complaint", "update-complaint", "assign-complaint"], "requiredSeedEntities": ["complaints", "workflowProfiles", "users"] },
  },
  {
    id: "complaints.cmts.complaints.new",
    path: "/cmts/complaints/new",
    title: "Create Complaint",
    module: "Complaint Management",
    feature: "complaints",
    permission: "COMPLAINT.CREATE",
    componentExport: "CmtsComplaintCreateContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "COMPLAINTS", "primaryActions": ["create-complaint", "update-complaint", "assign-complaint"], "requiredSeedEntities": ["complaints", "workflowProfiles", "users"] },
  },
  {
    id: "complaints.cmts.complaints.by-complaintid",
    path: "/cmts/complaints/:complaintId",
    title: "Complaint Detail",
    module: "Complaint Management",
    feature: "complaints",
    permission: "COMPLAINT.READ",
    componentExport: "CmtsComplaintDetailContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "COMPLAINTS", "primaryActions": ["create-complaint", "update-complaint", "assign-complaint"], "requiredSeedEntities": ["complaints", "workflowProfiles", "users"] },
  },
  {
    id: "workflow.cmts.complaints.by-complaintid.workflow",
    path: "/cmts/complaints/:complaintId/workflow",
    title: "Workflow",
    module: "Workflow",
    feature: "workflow",
    permission: "COMPLAINT.WORKFLOW",
    componentExport: "CmtsWorkflowContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "WORKFLOW", "primaryActions": ["execute-workflow"], "requiredSeedEntities": ["complaints", "workflowProfiles", "workflowActions"] },
  },
  {
    id: "complaints.cmts.complaints.by-complaintid.history",
    path: "/cmts/complaints/:complaintId/history",
    title: "Complaint History",
    module: "Complaint Management",
    feature: "complaints",
    permission: "COMPLAINT.READ",
    componentExport: "CmtsComplaintDetailContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "COMPLAINTS", "primaryActions": ["create-complaint", "update-complaint", "assign-complaint"], "requiredSeedEntities": ["complaints", "workflowProfiles", "users"] },
  },
  {
    id: "audit.cmts.complaints.by-complaintid.audit",
    path: "/cmts/complaints/:complaintId/audit",
    title: "Complaint Audit Trail",
    module: "Audit & Security",
    feature: "audit",
    permission: "AUDIT.READ",
    componentExport: "CmtsAuditLogsContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "AUDIT", "primaryActions": ["read-audit"], "requiredSeedEntities": ["auditEvents", "securityEvents"] },
  },
  {
    id: "rca.cmts.complaints.by-complaintid.rca",
    path: "/cmts/complaints/:complaintId/rca",
    title: "RCA",
    module: "RCA / CAPA",
    feature: "rca",
    permission: "RCA.READ",
    componentExport: "CmtsRcaContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "RCA", "primaryActions": ["create-rca", "submit-rca", "approve-rca"], "requiredSeedEntities": ["complaints", "rcaRecords", "workflowProfiles"] },
  },
  {
    id: "capa.cmts.complaints.by-complaintid.capa",
    path: "/cmts/complaints/:complaintId/capa",
    title: "CAPA",
    module: "RCA / CAPA",
    feature: "capa",
    permission: "CAPA.READ",
    componentExport: "CmtsCapaContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "CAPA", "primaryActions": ["create-capa", "submit-capa", "verify-capa"], "requiredSeedEntities": ["complaints", "capaRecords", "workflowProfiles"] },
  },
  {
    id: "verification.cmts.complaints.by-complaintid.verification",
    path: "/cmts/complaints/:complaintId/verification",
    title: "Verification",
    module: "RCA / CAPA",
    feature: "verification",
    permission: "VERIFICATION.READ",
    componentExport: "CmtsVerificationContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "VERIFICATION", "primaryActions": ["create-verification", "approve-verification"], "requiredSeedEntities": ["complaints", "verificationRecords", "workflowProfiles"] },
  },
  {
    id: "evidence.cmts.complaints.by-complaintid.evidence",
    path: "/cmts/complaints/:complaintId/evidence",
    title: "Evidence",
    module: "Evidence",
    feature: "evidence",
    permission: "COMPLAINT_EVIDENCE.UPLOAD_EVIDENCE",
    componentExport: "CmtsEvidenceContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "EVIDENCE", "primaryActions": ["upload-evidence"], "requiredSeedEntities": ["evidence", "complaints"] },
  },
  {
    id: "messages.cmts.complaints.by-complaintid.messages",
    path: "/cmts/complaints/:complaintId/messages",
    title: "Messages",
    module: "My Work",
    feature: "messages",
    permission: "MESSAGE.READ",
    componentExport: "CmtsMessagesContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "MESSAGES", "primaryActions": ["reply-message", "archive-message"], "requiredSeedEntities": ["messages", "complaints", "users"] },
  },
  {
    id: "tasks.cmts.complaints.by-complaintid.tasks",
    path: "/cmts/complaints/:complaintId/tasks",
    title: "Complaint Tasks",
    module: "My Work",
    feature: "tasks",
    permission: "TASK.READ",
    componentExport: "CmtsTasksContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "TASKS", "primaryActions": ["complete-task", "reassign-task"], "requiredSeedEntities": ["tasks", "complaints", "users"] },
  },
  {
    id: "sla.cmts.sla",
    path: "/cmts/sla",
    title: "SLA",
    module: "Complaint Management",
    feature: "sla",
    permission: "SLA.READ",
    componentExport: "CmtsSlaContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "SLA", "primaryActions": ["recalculate-sla"], "requiredSeedEntities": ["slaRecords", "complaints", "workingCalendars"] },
  },
  {
    id: "sla-tracker",
    path: "/cmts/complaints/sla",
    title: "SLA Tracker",
    module: "Complaint Management",
    feature: "sla",
    permission: "SLA.READ",
    componentExport: "CmtsSlaContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "complaint-management",
    menuGroup: "Complaint Management",
    menuOrder: 7,
    menuDescription: "SLA risk and breaches",
    operationSpine: { "domain": "SLA", "primaryActions": ["recalculate-sla"], "requiredSeedEntities": ["slaRecords", "complaints", "workingCalendars"] },
  },
  {
    id: "escalation.cmts.escalations",
    path: "/cmts/escalations",
    title: "Escalations",
    module: "Complaint Management",
    feature: "escalation",
    permission: "ESCALATION.READ",
    componentExport: "CmtsEscalationContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "ESCALATION", "primaryActions": ["acknowledge-escalation"], "requiredSeedEntities": ["escalations", "complaints", "users"] },
  },
  {
    id: "escalations",
    path: "/cmts/complaints/escalations",
    title: "Escalations",
    module: "Complaint Management",
    feature: "escalation",
    permission: "ESCALATION.READ",
    componentExport: "CmtsEscalationContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "complaint-management",
    menuGroup: "Complaint Management",
    menuOrder: 8,
    menuDescription: "Escalation queue",
    operationSpine: { "domain": "ESCALATION", "primaryActions": ["acknowledge-escalation"], "requiredSeedEntities": ["escalations", "complaints", "users"] },
  },
  {
    id: "rca.cmts.rca",
    path: "/cmts/rca",
    title: "RCA Queue",
    module: "RCA / CAPA",
    feature: "rca",
    permission: "RCA.READ",
    componentExport: "CmtsRcaContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "RCA", "primaryActions": ["create-rca", "submit-rca", "approve-rca"], "requiredSeedEntities": ["complaints", "rcaRecords", "workflowProfiles"] },
  },
  {
    id: "rca-capa.cmts.rca-capa",
    path: "/cmts/rca-capa",
    title: "RCA / CAPA",
    module: "Rca Capa",
    feature: "rca-capa",
    permission: "RCA.READ",
    componentExport: "CmtsRcaContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "RCA_CAPA", "primaryActions": ["open-rca-capa-workspace"], "requiredSeedEntities": ["rcaRecords", "capaRecords", "verificationRecords"] },
  },
  {
    id: "rca-dashboard",
    path: "/cmts/rca-capa/dashboard",
    title: "Dashboard",
    module: "RCA / CAPA",
    feature: "rca-dashboard",
    permission: "RCA.READ",
    componentExport: "CmtsRcaContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "rca-capa",
    menuGroup: "RCA / CAPA",
    menuOrder: 9,
    menuDescription: "Real-time investigation analytics & metrics",
    personaLandingFor: [CMTS_ROLE_CODES.RCA_LEAD],
    operationSpine: { "domain": "RCA", "primaryActions": ["view-rca-dashboard"], "requiredSeedEntities": ["rcaRecords"] },
  },
  {
    id: "rca-queue",
    path: "/cmts/rca-capa/rca-queue",
    title: "RCA Records",
    module: "RCA / CAPA",
    feature: "rca",
    permission: "RCA.READ",
    componentExport: "CmtsRcaContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "rca-capa",
    menuGroup: "RCA / CAPA",
    menuOrder: 10,
    menuDescription: "RCA investigations and method workspaces",
    personaLandingFor: [CMTS_ROLE_CODES.RCA_LEAD],
    operationSpine: { "domain": "RCA", "primaryActions": ["create-rca", "submit-rca", "approve-rca"], "requiredSeedEntities": ["complaints", "rcaRecords", "workflowProfiles"] },
  },
  {
    id: "capa-queue",
    path: "/cmts/rca-capa/capa-queue",
    title: "CAPA Actions",
    module: "CAPA",
    feature: "capa",
    permission: "CAPA.READ",
    componentExport: "CmtsCapaContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "capa",
    menuGroup: "CAPA",
    menuOrder: 1,
    menuDescription: "Corrective and preventive action tracking",
    personaLandingFor: [CMTS_ROLE_CODES.CAPA_OWNER],
    operationSpine: { "domain": "CAPA", "primaryActions": ["create-capa", "submit-capa", "verify-capa"], "requiredSeedEntities": ["complaints", "capaRecords", "workflowProfiles"] },
  },
  {
    id: "rca-reports",
    path: "/cmts/rca-capa/reports",
    title: "Reports",
    module: "RCA / CAPA",
    feature: "rca-reports",
    permission: "RCA.READ",
    componentExport: "CmtsRcaContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "rca-capa",
    menuGroup: "RCA / CAPA",
    menuOrder: 12,
    menuDescription: "Pareto analytics & MTTR reports",
    operationSpine: { "domain": "RCA", "primaryActions": ["view-rca-reports"], "requiredSeedEntities": ["rcaRecords"] },
  },
  {
    id: "rca-fmea",
    path: "/cmts/rca-capa/fmea",
    title: "FMEA",
    module: "RCA / CAPA",
    feature: "rca-fmea",
    permission: "RCA.READ",
    componentExport: "CmtsRcaContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "rca-capa",
    menuGroup: "RCA / CAPA",
    menuOrder: 13,
    menuDescription: "Risk registry and AIAG/VDA AP tracking",
    operationSpine: { "domain": "RCA", "primaryActions": ["view-fmea-registry"], "requiredSeedEntities": ["rcaRecords"] },
  },
  {
    id: "rca-settings",
    path: "/cmts/rca-capa/settings",
    title: "Settings",
    module: "RCA / CAPA",
    feature: "rca-settings",
    permission: "RCA.READ",
    componentExport: "CmtsRcaContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "rca-capa",
    menuGroup: "RCA / CAPA",
    menuOrder: 14,
    menuDescription: "SLA thresholds & SoD governance",
    operationSpine: { "domain": "RCA", "primaryActions": ["configure-rca-settings"], "requiredSeedEntities": ["workflowProfiles"] },
  },
  {
    id: "inv-overview",
    path: "/cmts/rca-capa/investigation/overview",
    title: "4. Overview",
    module: "Investigation Navigation",
    feature: "rca-investigation",
    permission: "RCA.READ",
    componentExport: "CmtsRcaContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "investigation-navigation",
    menuGroup: "Investigation Navigation",
    menuOrder: 1,
    menuDescription: "Investigation scope & lifecycle overview",
    operationSpine: { "domain": "RCA", "primaryActions": ["view-rca-overview"], "requiredSeedEntities": ["rcaRecords"] },
  },
  {
    id: "inv-method",
    path: "/cmts/rca-capa/investigation/method",
    title: "Method (FISHBONE)",
    module: "Investigation Navigation",
    feature: "rca-investigation",
    permission: "RCA.READ",
    componentExport: "CmtsRcaContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "investigation-navigation",
    menuGroup: "Investigation Navigation",
    menuOrder: 2,
    menuDescription: "8 RCA method workspaces",
    operationSpine: { "domain": "RCA", "primaryActions": ["execute-rca-method"], "requiredSeedEntities": ["rcaRecords"] },
  },
  {
    id: "inv-evidence",
    path: "/cmts/rca-capa/investigation/evidence",
    title: "5. Evidence Center (3)",
    module: "Investigation Navigation",
    feature: "rca-investigation",
    permission: "RCA.READ",
    componentExport: "CmtsRcaContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "investigation-navigation",
    menuGroup: "Investigation Navigation",
    menuOrder: 3,
    menuDescription: "Cryptographic SHA-256 evidence center",
    operationSpine: { "domain": "RCA", "primaryActions": ["manage-evidence"], "requiredSeedEntities": ["rcaRecords"] },
  },
  {
    id: "inv-actions",
    path: "/cmts/rca-capa/investigation/actions",
    title: "6. Actions / CAPA (3)",
    module: "Investigation Navigation",
    feature: "rca-investigation",
    permission: "RCA.READ",
    componentExport: "CmtsRcaContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "investigation-navigation",
    menuGroup: "Investigation Navigation",
    menuOrder: 4,
    menuDescription: "Corrective and preventive actions with CAPA handoff",
    operationSpine: { "domain": "RCA", "primaryActions": ["manage-actions"], "requiredSeedEntities": ["rcaRecords"] },
  },
  {
    id: "inv-team",
    path: "/cmts/rca-capa/investigation/team",
    title: "7. Team & Collab (5)",
    module: "Investigation Navigation",
    feature: "rca-investigation",
    permission: "RCA.READ",
    componentExport: "CmtsRcaContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "investigation-navigation",
    menuGroup: "Investigation Navigation",
    menuOrder: 5,
    menuDescription: "Team roles & investigation notes",
    operationSpine: { "domain": "RCA", "primaryActions": ["manage-team"], "requiredSeedEntities": ["rcaRecords"] },
  },
  {
    id: "inv-review",
    path: "/cmts/rca-capa/investigation/review",
    title: "8. Review / Approval",
    module: "Investigation Navigation",
    feature: "rca-investigation",
    permission: "RCA.READ",
    componentExport: "CmtsRcaContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "investigation-navigation",
    menuGroup: "Investigation Navigation",
    menuOrder: 6,
    menuDescription: "Formal QA review checklist & SoD approval",
    operationSpine: { "domain": "RCA", "primaryActions": ["review-rca"], "requiredSeedEntities": ["rcaRecords"] },
  },
  {
    id: "inv-audit",
    path: "/cmts/rca-capa/investigation/audit",
    title: "9. History / Audit",
    module: "Investigation Navigation",
    feature: "rca-investigation",
    permission: "RCA.READ",
    componentExport: "CmtsRcaContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "investigation-navigation",
    menuGroup: "Investigation Navigation",
    menuOrder: 7,
    menuDescription: "Audit trail log",
    operationSpine: { "domain": "RCA", "primaryActions": ["view-audit-trail"], "requiredSeedEntities": ["rcaRecords"] },
  },
  {
    id: "inv-fmea",
    path: "/cmts/rca-capa/investigation/fmea-feedback",
    title: "18. FMEA Feedback",
    module: "Investigation Navigation",
    feature: "rca-investigation",
    permission: "RCA.READ",
    componentExport: "CmtsRcaContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "investigation-navigation",
    menuGroup: "Investigation Navigation",
    menuOrder: 8,
    menuDescription: "Process & Design FMEA risk feedback loop",
    operationSpine: { "domain": "RCA", "primaryActions": ["link-fmea"], "requiredSeedEntities": ["rcaRecords"] },
  },
  {
    id: "inv-lifecycle",
    path: "/cmts/rca-capa/investigation/lifecycle",
    title: "10. Hold / Cancel",
    module: "Investigation Navigation",
    feature: "rca-investigation",
    permission: "RCA.READ",
    componentExport: "CmtsRcaContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "investigation-navigation",
    menuGroup: "Investigation Navigation",
    menuOrder: 9,
    menuDescription: "Lifecycle transitions",
    operationSpine: { "domain": "RCA", "primaryActions": ["manage-lifecycle"], "requiredSeedEntities": ["rcaRecords"] },
  },
  {
    id: "verification-queue",
    path: "/cmts/rca-capa/verification-queue",
    title: "Verification Queue",
    module: "Verification",
    feature: "verification",
    permission: "VERIFICATION.READ",
    componentExport: "CmtsVerificationContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "verification",
    menuGroup: "Verification",
    menuOrder: 1,
    menuDescription: "Verification and closure approval",
    personaLandingFor: [CMTS_ROLE_CODES.VERIFICATION_OWNER],
    operationSpine: { "domain": "VERIFICATION", "primaryActions": ["create-verification", "approve-verification"], "requiredSeedEntities": ["complaints", "verificationRecords", "workflowProfiles"] },
  },
  {
    id: "rca.cmts.rca-capa.rca",
    path: "/cmts/rca-capa/rca",
    title: "RCA Workspace",
    module: "RCA / CAPA",
    feature: "rca",
    permission: "RCA.READ",
    componentExport: "CmtsRcaContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "RCA", "primaryActions": ["create-rca", "submit-rca", "approve-rca"], "requiredSeedEntities": ["complaints", "rcaRecords", "workflowProfiles"] },
  },
  {
    id: "capa.cmts.rca-capa.capa",
    path: "/cmts/rca-capa/capa",
    title: "CAPA Workspace",
    module: "RCA / CAPA",
    feature: "capa",
    permission: "CAPA.READ",
    componentExport: "CmtsCapaContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "CAPA", "primaryActions": ["create-capa", "submit-capa", "verify-capa"], "requiredSeedEntities": ["complaints", "capaRecords", "workflowProfiles"] },
  },
  {
    id: "verification.cmts.rca-capa.verification",
    path: "/cmts/rca-capa/verification",
    title: "Verification Workspace",
    module: "RCA / CAPA",
    feature: "verification",
    permission: "VERIFICATION.READ",
    componentExport: "CmtsVerificationContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "VERIFICATION", "primaryActions": ["create-verification", "approve-verification"], "requiredSeedEntities": ["complaints", "verificationRecords", "workflowProfiles"] },
  },
  {
    id: "rca.cmts.rca-capa.rca.by-recordid",
    path: "/cmts/rca-capa/rca/:recordId",
    title: "RCA Detail",
    module: "RCA / CAPA",
    feature: "rca",
    permission: "RCA.READ",
    componentExport: "CmtsRcaContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "RCA", "primaryActions": ["create-rca", "submit-rca", "approve-rca"], "requiredSeedEntities": ["complaints", "rcaRecords", "workflowProfiles"] },
  },
  {
    id: "capa.cmts.rca-capa.capa.by-recordid",
    path: "/cmts/rca-capa/capa/:recordId",
    title: "CAPA Detail",
    module: "RCA / CAPA",
    feature: "capa",
    permission: "CAPA.READ",
    componentExport: "CmtsCapaContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "CAPA", "primaryActions": ["create-capa", "submit-capa", "verify-capa"], "requiredSeedEntities": ["complaints", "capaRecords", "workflowProfiles"] },
  },
  {
    id: "verification.cmts.rca-capa.verification.by-recordid",
    path: "/cmts/rca-capa/verification/:recordId",
    title: "Verification Detail",
    module: "RCA / CAPA",
    feature: "verification",
    permission: "VERIFICATION.READ",
    componentExport: "CmtsVerificationContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "VERIFICATION", "primaryActions": ["create-verification", "approve-verification"], "requiredSeedEntities": ["complaints", "verificationRecords", "workflowProfiles"] },
  },
  {
    id: "capa.cmts.capa",
    path: "/cmts/capa",
    title: "CAPA Queue",
    module: "RCA / CAPA",
    feature: "capa",
    permission: "CAPA.READ",
    componentExport: "CmtsCapaContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "CAPA", "primaryActions": ["create-capa", "submit-capa", "verify-capa"], "requiredSeedEntities": ["complaints", "capaRecords", "workflowProfiles"] },
  },
  {
    id: "verification.cmts.verification",
    path: "/cmts/verification",
    title: "Verification Queue",
    module: "RCA / CAPA",
    feature: "verification",
    permission: "VERIFICATION.READ",
    componentExport: "CmtsVerificationContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "VERIFICATION", "primaryActions": ["create-verification", "approve-verification"], "requiredSeedEntities": ["complaints", "verificationRecords", "workflowProfiles"] },
  },
  {
    id: "alerts.cmts.alerts",
    path: "/cmts/alerts",
    title: "Alerts",
    module: "My Work",
    feature: "alerts",
    permission: "ALERT.READ",
    componentExport: "CmtsAlertsContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "ALERTS", "primaryActions": ["acknowledge-alert"], "requiredSeedEntities": ["alerts", "complaints"] },
  },
  {
    id: "notifications",
    path: "/cmts/notifications",
    title: "Notifications",
    module: "My Work",
    feature: "notifications",
    permission: "NOTIFICATION.READ",
    componentExport: "CmtsNotificationsContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "my-work",
    menuGroup: "My Work",
    menuOrder: 3,
    menuDescription: "Alerts and reminders",
    operationSpine: { "domain": "NOTIFICATIONS", "primaryActions": ["mark-notification-read", "acknowledge-notification"], "requiredSeedEntities": ["notifications", "complaints", "users"] },
  },
  {
    id: "reports",
    path: "/cmts/reports",
    title: "Reports",
    module: "Reports",
    feature: "reports",
    permission: "REPORT.READ",
    componentExport: "CmtsReportsContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "reports",
    menuGroup: "Reports",
    menuOrder: 18,
    menuDescription: "Report catalog",
    personaLandingFor: ["REPORT_MANAGER"],
    operationSpine: { "domain": "REPORTS", "primaryActions": ["run-report", "export-report"], "requiredSeedEntities": ["reportDefinitions", "reportRows", "reportExports"] },
  },
  {
    id: "reports.cmts.reports.catalog",
    path: "/cmts/reports/catalog",
    title: "Report Catalog",
    module: "Reports",
    feature: "reports",
    permission: "REPORT.READ",
    componentExport: "CmtsReportsContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "REPORTS", "primaryActions": ["run-report", "export-report"], "requiredSeedEntities": ["reportDefinitions", "reportRows", "reportExports"] },
  },
  {
    id: "typed-reports",
    path: "/cmts/reports/typed",
    title: "Typed Reports",
    module: "Reports",
    feature: "reports",
    permission: "REPORT.READ",
    componentExport: "CmtsTypedReportsContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "reports",
    menuGroup: "Reports",
    menuOrder: 19,
    menuDescription: "Curated typed reports",
    operationSpine: { "domain": "REPORTS", "primaryActions": ["run-report", "export-report"], "requiredSeedEntities": ["reportDefinitions", "reportRows", "reportExports"] },
  },
  {
    id: "reports.cmts.reports.preview",
    path: "/cmts/reports/preview",
    title: "Report Preview",
    module: "Reports",
    feature: "reports",
    permission: "REPORT.READ",
    componentExport: "CmtsTypedReportsContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "REPORTS", "primaryActions": ["run-report", "export-report"], "requiredSeedEntities": ["reportDefinitions", "reportRows", "reportExports"] },
  },
  {
    id: "sla-reports",
    path: "/cmts/reports/sla",
    title: "SLA Reports",
    module: "Reports",
    feature: "reports",
    permission: "REPORT.READ",
    componentExport: "CmtsTypedReportsContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "reports",
    menuGroup: "Reports",
    menuOrder: 20,
    menuPath: "/cmts/reports/sla?reportCode=sla-breach",
    menuDescription: "SLA breach and near-breach",
    operationSpine: { "domain": "REPORTS", "primaryActions": ["run-report", "export-report"], "requiredSeedEntities": ["reportDefinitions", "reportRows", "reportExports"] },
  },
  {
    id: "ageing-report",
    path: "/cmts/reports/ageing",
    title: "Ageing Report",
    module: "Reports",
    feature: "reports",
    permission: "REPORT.READ",
    componentExport: "CmtsTypedReportsContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "reports",
    menuGroup: "Reports",
    menuOrder: 21,
    menuPath: "/cmts/reports/ageing?reportCode=ageing",
    menuDescription: "Complaint ageing and stage dwell",
    operationSpine: { "domain": "REPORTS", "primaryActions": ["run-report", "export-report"], "requiredSeedEntities": ["reportDefinitions", "reportRows", "reportExports"] },
  },
  {
    id: "department-performance-report",
    path: "/cmts/reports/department-performance",
    title: "Department Performance",
    module: "Reports",
    feature: "reports",
    permission: "REPORT.READ",
    componentExport: "CmtsTypedReportsContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "reports",
    menuGroup: "Reports",
    menuOrder: 22,
    menuPath: "/cmts/reports/department-performance?reportCode=department-performance",
    menuDescription: "Department closure, SLA and workload performance",
    operationSpine: { "domain": "REPORTS", "primaryActions": ["run-report", "export-report"], "requiredSeedEntities": ["reportDefinitions", "reportRows", "reportExports"] },
  },
  {
    id: "customer-report",
    path: "/cmts/reports/customer",
    title: "Customer Reports",
    module: "Reports",
    feature: "reports",
    permission: "REPORT.READ",
    componentExport: "CmtsTypedReportsContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "reports",
    menuGroup: "Reports",
    menuOrder: 23,
    menuPath: "/cmts/reports/customer?reportCode=customer",
    menuDescription: "Customer complaint and closure reporting",
    operationSpine: { "domain": "REPORTS", "primaryActions": ["run-report", "export-report"], "requiredSeedEntities": ["reportDefinitions", "reportRows", "reportExports"] },
  },
  {
    id: "supplier-report",
    path: "/cmts/reports/supplier",
    title: "Supplier Reports",
    module: "Reports",
    feature: "reports",
    permission: "REPORT.READ",
    componentExport: "CmtsTypedReportsContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "reports",
    menuGroup: "Reports",
    menuOrder: 24,
    menuPath: "/cmts/reports/supplier?reportCode=supplier",
    menuDescription: "Supplier complaint, RCA and CAPA reporting",
    operationSpine: { "domain": "REPORTS", "primaryActions": ["run-report", "export-report"], "requiredSeedEntities": ["reportDefinitions", "reportRows", "reportExports"] },
  },
  {
    id: "rca-capa-reports",
    path: "/cmts/reports/rca-capa",
    title: "RCA / CAPA Reports",
    module: "Reports",
    feature: "reports",
    permission: "REPORT.READ",
    componentExport: "CmtsTypedReportsContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "reports",
    menuGroup: "Reports",
    menuOrder: 25,
    menuPath: "/cmts/reports/rca-capa?reportCode=rca-capa",
    menuDescription: "RCA CAPA linkage",
    operationSpine: { "domain": "REPORTS", "primaryActions": ["run-report", "export-report"], "requiredSeedEntities": ["reportDefinitions", "reportRows", "reportExports"] },
  },
  {
    id: "closure-verification-report",
    path: "/cmts/reports/closure-verification",
    title: "Closure Verification",
    module: "Reports",
    feature: "reports",
    permission: "REPORT.READ",
    componentExport: "CmtsTypedReportsContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "reports",
    menuGroup: "Reports",
    menuOrder: 26,
    menuPath: "/cmts/reports/closure-verification?reportCode=closure-verification",
    menuDescription: "Verification and closure readiness",
    operationSpine: { "domain": "REPORTS", "primaryActions": ["run-report", "export-report"], "requiredSeedEntities": ["reportDefinitions", "reportRows", "reportExports"] },
  },
  {
    id: "audit-report",
    path: "/cmts/reports/audit",
    title: "Audit Report",
    module: "Reports",
    feature: "reports",
    permission: "AUDIT.READ",
    componentExport: "CmtsTypedReportsContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "reports",
    menuGroup: "Reports",
    menuOrder: 27,
    menuPath: "/cmts/reports/audit?reportCode=audit-activity",
    menuDescription: "Audit activity reporting",
    operationSpine: { "domain": "REPORTS", "primaryActions": ["run-report", "export-report"], "requiredSeedEntities": ["reportDefinitions", "reportRows", "reportExports"] },
  },
  {
    id: "security-report",
    path: "/cmts/reports/security",
    title: "Security Report",
    module: "Reports",
    feature: "reports",
    permission: "SECURITY.READ",
    componentExport: "CmtsTypedReportsContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "reports",
    menuGroup: "Reports",
    menuOrder: 28,
    menuPath: "/cmts/reports/security?reportCode=security-events",
    menuDescription: "Security and blocked action reporting",
    operationSpine: { "domain": "REPORTS", "primaryActions": ["run-report", "export-report"], "requiredSeedEntities": ["reportDefinitions", "reportRows", "reportExports"] },
  },
  {
    id: "report-exports",
    path: "/cmts/reports/exports",
    title: "Report Exports",
    module: "Reports",
    feature: "reports",
    permission: "REPORT.DOWNLOAD",
    componentExport: "CmtsReportExportsContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "REPORTS", "primaryActions": ["run-report", "export-report"], "requiredSeedEntities": ["reportDefinitions", "reportRows", "reportExports"] },
  },
  {
    id: "report-export-history",
    path: "/cmts/reports/export-history",
    title: "Export History",
    module: "Reports",
    feature: "reports",
    permission: "REPORT.DOWNLOAD",
    componentExport: "CmtsReportExportsContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "reports",
    menuGroup: "Reports",
    menuOrder: 30,
    menuDescription: "Export audit history",
    operationSpine: { "domain": "REPORTS", "primaryActions": ["run-report", "export-report"], "requiredSeedEntities": ["reportDefinitions", "reportRows", "reportExports"] },
  },
  {
    id: "reports.cmts.reports.scheduled",
    path: "/cmts/reports/scheduled",
    title: "Scheduled Reports",
    module: "Reports",
    feature: "reports",
    permission: "REPORT.DOWNLOAD",
    componentExport: "CmtsReportExportsContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "REPORTS", "primaryActions": ["run-report", "export-report"], "requiredSeedEntities": ["reportDefinitions", "reportRows", "reportExports"] },
  },
  {
    id: "reports.cmts.report-exports",
    path: "/cmts/report-exports",
    title: "Report Exports",
    module: "Reports",
    feature: "reports",
    permission: "REPORT.DOWNLOAD",
    componentExport: "CmtsReportExportsContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "REPORTS", "primaryActions": ["run-report", "export-report"], "requiredSeedEntities": ["reportDefinitions", "reportRows", "reportExports"] },
  },
  {
    id: "dashboard.cmts.dashboards",
    path: "/cmts/dashboards",
    title: "Dashboards",
    module: "Dashboards",
    feature: "dashboard",
    permission: "DASHBOARD.READ",
    componentExport: "CmtsDashboardsContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "DASHBOARD", "primaryActions": ["open-drilldown"], "requiredSeedEntities": ["dashboardSignals", "complaints", "tasks"] },
  },
  {
    id: "dashboard.cmts.dashboards.system",
    path: "/cmts/dashboards/system",
    title: "System Dashboard",
    module: "Dashboards",
    feature: "dashboard",
    permission: "DASHBOARD.READ",
    componentExport: "CmtsDashboardsContainer",
    showInMenu: false,
    requiresTenantScope: true,
    personaLandingFor: [CMTS_ROLE_CODES.SYSTEM_ADMIN],
    operationSpine: { "domain": "DASHBOARD", "primaryActions": ["open-drilldown"], "requiredSeedEntities": ["dashboardSignals", "complaints", "tasks"] },
  },
  {
    id: "dashboard.cmts.dashboards.admin",
    path: "/cmts/dashboards/admin",
    title: "Admin Dashboard",
    module: "Dashboards",
    feature: "dashboard",
    permission: "DASHBOARD.READ",
    componentExport: "CmtsDashboardsContainer",
    showInMenu: false,
    requiresTenantScope: true,
    personaLandingFor: [CMTS_ROLE_CODES.TENANT_ADMIN],
    operationSpine: { "domain": "DASHBOARD", "primaryActions": ["open-drilldown"], "requiredSeedEntities": ["dashboardSignals", "complaints", "tasks"] },
  },
  {
    id: "quality-dashboard",
    path: "/cmts/dashboards/quality",
    title: "Quality Dashboard",
    module: "Dashboards",
    feature: "dashboard",
    permission: "DASHBOARD.QUALITY.READ",
    componentExport: "CmtsDashboardsContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "dashboards",
    menuGroup: "Dashboards",
    menuOrder: 13,
    menuDescription: "Quality operations",
    personaLandingFor: [CMTS_ROLE_CODES.QUALITY_HEAD],
    operationSpine: { "domain": "DASHBOARD", "primaryActions": ["open-drilldown"], "requiredSeedEntities": ["dashboardSignals", "complaints", "tasks"] },
  },
  {
    id: "management-dashboard",
    path: "/cmts/dashboards/management",
    title: "Management Dashboard",
    module: "Dashboards",
    feature: "dashboard",
    permission: "DASHBOARD.MANAGEMENT.READ",
    componentExport: "CmtsDashboardsContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "dashboards",
    menuGroup: "Dashboards",
    menuOrder: 12,
    menuDescription: "Executive KPIs",
    personaLandingFor: [CMTS_ROLE_CODES.MANAGEMENT],
    operationSpine: { "domain": "DASHBOARD", "primaryActions": ["open-drilldown"], "requiredSeedEntities": ["dashboardSignals", "complaints", "tasks"] },
  },
  {
    id: "department-dashboard",
    path: "/cmts/dashboards/department",
    title: "Department Dashboard",
    module: "Dashboards",
    feature: "dashboard",
    permission: "DASHBOARD.DEPARTMENT.READ",
    componentExport: "CmtsDashboardsContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "dashboards",
    menuGroup: "Dashboards",
    menuOrder: 14,
    menuDescription: "Department workload",
    personaLandingFor: [CMTS_ROLE_CODES.QUALITY_MANAGER, CMTS_ROLE_CODES.DEPARTMENT_MANAGER],
    operationSpine: { "domain": "DASHBOARD", "primaryActions": ["open-drilldown"], "requiredSeedEntities": ["dashboardSignals", "complaints", "tasks"] },
  },
  {
    id: "personal-dashboard",
    path: "/cmts/dashboards/personal",
    title: "Personal Dashboard",
    module: "Dashboards",
    feature: "dashboard",
    permission: "DASHBOARD.PERSONAL.READ",
    componentExport: "CmtsDashboardsContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "dashboards",
    menuGroup: "Dashboards",
    menuOrder: 15,
    menuDescription: "Individual queue",
    operationSpine: { "domain": "DASHBOARD", "primaryActions": ["open-drilldown"], "requiredSeedEntities": ["dashboardSignals", "complaints", "tasks"] },
  },
  {
    id: "customer-dashboard",
    path: "/cmts/dashboards/customer",
    title: "Customer Dashboard",
    module: "Dashboards",
    feature: "dashboard",
    permission: "DASHBOARD.CUSTOMER.READ",
    componentExport: "CmtsDashboardsContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "dashboards",
    menuGroup: "Dashboards",
    menuOrder: 16,
    menuDescription: "Customer-safe view",
    personaLandingFor: [CMTS_ROLE_CODES.CUSTOMER_USER],
    operationSpine: { "domain": "DASHBOARD", "primaryActions": ["open-drilldown"], "requiredSeedEntities": ["dashboardSignals", "complaints", "tasks"] },
  },
  {
    id: "supplier-dashboard",
    path: "/cmts/dashboards/supplier",
    title: "Supplier Dashboard",
    module: "Dashboards",
    feature: "dashboard",
    permission: "DASHBOARD.SUPPLIER.READ",
    componentExport: "CmtsDashboardsContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "dashboards",
    menuGroup: "Dashboards",
    menuOrder: 17,
    menuDescription: "Supplier-safe view",
    personaLandingFor: [CMTS_ROLE_CODES.SUPPLIER_USER],
    operationSpine: { "domain": "DASHBOARD", "primaryActions": ["open-drilldown"], "requiredSeedEntities": ["dashboardSignals", "complaints", "tasks"] },
  },
  {
    id: "master-data",
    path: "/cmts/admin/master-data",
    title: "Master Data",
    module: "Administration",
    feature: "admin",
    permission: "MASTER_DATA.READ",
    componentExport: "CmtsMasterDataAdminContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["open-master-data"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 31,
    menuDescription: "Core setup",
  },
  {
    id: "master-data.complaint-sources",
    path: "/cmts/admin/master-data/complaint-sources",
    title: "Complaint Sources",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsComplaintSourceContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories", "reportDefinitions", "dashboardKpis"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.01,
    menuDescription: "Source/channel values",
  },
  {
    id: "master-data.complaint-types",
    path: "/cmts/admin/master-data/complaint-types",
    title: "Complaint Types",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsMasterDataAdminContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories", "reportDefinitions", "dashboardKpis"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.02,
    menuDescription: "Complaint type classification",
  },
  {
    id: "master-data.complaint-categories",
    path: "/cmts/admin/master-data/complaint-categories",
    title: "Complaint Categories",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsComplaintCategoryContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories", "reportDefinitions", "dashboardKpis"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.03,
    menuDescription: "Primary complaint category values",
  },
  {
    id: "master-data.complaint-subcategories",
    path: "/cmts/admin/master-data/complaint-subcategories",
    title: "Complaint Subcategories",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsComplaintSubcategoryContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories", "reportDefinitions", "dashboardKpis"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.04,
    menuDescription: "Subcategory values linked to parent category",
  },
  {
    id: "master-data.priorities",
    path: "/cmts/admin/master-data/priorities",
    title: "Priorities",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsPriorityContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories", "reportDefinitions", "dashboardKpis"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.05,
    menuDescription: "Complaint priority values",
  },
  {
    id: "master-data.severities",
    path: "/cmts/admin/master-data/severities",
    title: "Severities",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsSeverityContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories", "reportDefinitions", "dashboardKpis"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.06,
    menuDescription: "Complaint severity values",
  },
  {
    id: "master-data.reference-types",
    path: "/cmts/admin/master-data/reference-types",
    title: "Reference Types",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "ReferenceTypeContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "referenceTypes", "referenceData"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.061,
    menuDescription: "Reference type definitions",
  },
  {
    id: "master-data.reference-data",
    path: "/cmts/admin/master-data/reference-data",
    title: "Reference Data",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsReferenceDataContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "referenceTypes", "referenceData"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.062,
    menuDescription: "Reference values linked to reference types",
  },
  {
    id: "master-data.workflow-statuses",
    path: "/cmts/admin/master-data/workflow-statuses",
    title: "Workflow Statuses",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsMasterDataAdminContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories", "reportDefinitions", "dashboardKpis"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.07,
    menuDescription: "Workflow lifecycle statuses",
  },
  {
    id: "master-data.workflow-stages",
    path: "/cmts/admin/master-data/workflow-stages",
    title: "Workflow Stages",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsMasterDataAdminContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories", "reportDefinitions", "dashboardKpis"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.08,
    menuDescription: "Workflow lifecycle stages",
  },
  {
    id: "master-data.rca-methods",
    path: "/cmts/admin/master-data/rca-methods",
    title: "RCA Methods",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsMasterDataAdminContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories", "reportDefinitions", "dashboardKpis"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.09,
    menuDescription: "RCA method values",
  },
  {
    id: "master-data.root-cause-categories",
    path: "/cmts/admin/master-data/root-cause-categories",
    title: "Root Cause Categories",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsMasterDataAdminContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories", "reportDefinitions", "dashboardKpis"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.1,
    menuDescription: "Root cause taxonomy",
  },
  {
    id: "master-data.capa-action-types",
    path: "/cmts/admin/master-data/capa-action-types",
    title: "CAPA Action Types",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsMasterDataAdminContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories", "reportDefinitions", "dashboardKpis"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.11,
    menuDescription: "CAPA action types",
  },
  {
    id: "master-data.verification-methods",
    path: "/cmts/admin/master-data/verification-methods",
    title: "Verification Methods",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsMasterDataAdminContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories", "reportDefinitions", "dashboardKpis"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.12,
    menuDescription: "Verification method values",
  },
  {
    id: "master-data.evidence-types",
    path: "/cmts/admin/master-data/evidence-types",
    title: "Evidence Types",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsMasterDataAdminContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories", "reportDefinitions", "dashboardKpis"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.13,
    menuDescription: "Evidence types",
  },
  {
    id: "master-data.defect-modes",
    path: "/cmts/admin/master-data/defect-modes",
    title: "Defect Modes",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsMasterDataAdminContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories", "reportDefinitions", "dashboardKpis"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.14,
    menuDescription: "Defect modes",
  },
  {
    id: "master-data.failure-modes",
    path: "/cmts/admin/master-data/failure-modes",
    title: "Failure Modes",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsMasterDataAdminContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories", "reportDefinitions", "dashboardKpis"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.15,
    menuDescription: "Failure modes",
  },
  {
    id: "master-data.containment-types",
    path: "/cmts/admin/master-data/containment-types",
    title: "Containment Types",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsMasterDataAdminContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories", "reportDefinitions", "dashboardKpis"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.16,
    menuDescription: "Containment types",
  },
  {
    id: "master-data.reason-codes",
    path: "/cmts/admin/master-data/reason-codes",
    title: "Reason Codes",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsMasterDataAdminContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories", "reportDefinitions", "dashboardKpis"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.17,
    menuDescription: "Reason codes",
  },
  {
    id: "master-data.closure-codes",
    path: "/cmts/admin/master-data/closure-codes",
    title: "Closure Codes",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsMasterDataAdminContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories", "reportDefinitions", "dashboardKpis"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.18,
    menuDescription: "Closure codes",
  },
  {
    id: "master-data.departments",
    path: "/cmts/admin/master-data/departments",
    title: "Departments",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsMasterDataAdminContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories", "reportDefinitions", "dashboardKpis"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.19,
    menuDescription: "Department master",
  },
  {
    id: "master-data.sites",
    path: "/cmts/admin/master-data/sites",
    title: "Sites / Plants",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsMasterDataAdminContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories", "reportDefinitions", "dashboardKpis"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.2,
    menuDescription: "Site and plant master",
  },
  {
    id: "master-data.product-families",
    path: "/cmts/admin/master-data/product-families",
    title: "Product Families",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsMasterDataAdminContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories", "reportDefinitions", "dashboardKpis"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.21,
    menuDescription: "Product family master",
  },
  {
    id: "master-data.products",
    path: "/cmts/admin/master-data/products",
    title: "Products",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsMasterDataAdminContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories", "reportDefinitions", "dashboardKpis"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.22,
    menuDescription: "Product master",
  },
  {
    id: "master-data.parts",
    path: "/cmts/admin/master-data/parts",
    title: "Parts",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsMasterDataAdminContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories", "reportDefinitions", "dashboardKpis"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.23,
    menuDescription: "Part master",
  },
  {
    id: "master-data.processes",
    path: "/cmts/admin/master-data/processes",
    title: "Processes",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsMasterDataAdminContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories", "reportDefinitions", "dashboardKpis"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.24,
    menuDescription: "Process master",
  },
  {
    id: "master-data.lines",
    path: "/cmts/admin/master-data/lines",
    title: "Lines",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsMasterDataAdminContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories", "reportDefinitions", "dashboardKpis"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.25,
    menuDescription: "Line master",
  },
  {
    id: "master-data.machines",
    path: "/cmts/admin/master-data/machines",
    title: "Machines",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsMasterDataAdminContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories", "reportDefinitions", "dashboardKpis"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.26,
    menuDescription: "Machine master",
  },
  {
    id: "master-data.notification-templates",
    path: "/cmts/admin/master-data/notification-templates",
    title: "Notification Templates",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsMasterDataAdminContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories", "reportDefinitions", "dashboardKpis"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.27,
    menuDescription: "Notification templates",
  },
  {
    id: "master-data.message-templates",
    path: "/cmts/admin/master-data/message-templates",
    title: "Message Templates",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsMasterDataAdminContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories", "reportDefinitions", "dashboardKpis"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.28,
    menuDescription: "Message templates",
  },
  {
    id: "master-data.report-types",
    path: "/cmts/admin/master-data/report-types",
    title: "Report Types",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsMasterDataAdminContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories", "reportDefinitions", "dashboardKpis"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.29,
    menuDescription: "Report types",
  },
  {
    id: "master-data.dashboard-kpis",
    path: "/cmts/admin/master-data/dashboard-kpis",
    title: "Dashboard KPI Definitions",
    module: "Administration",
    feature: "admin",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsMasterDataAdminContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_MASTER_DATA", "primaryActions": ["create-master", "edit-master", "deactivate-master", "reactivate-master"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories", "reportDefinitions", "dashboardKpis"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.3,
    menuDescription: "Dashboard KPI definitions",
  },
  {
    id: "governance.cmts.governance",
    path: "/cmts/governance",
    title: "Governance",
    module: "Governance",
    feature: "governance",
    permission: "MASTER_DEPENDENCY.READ",
    componentExport: "CmtsSodAdminContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "GOVERNANCE", "primaryActions": ["open-governance"], "requiredSeedEntities": ["sodPolicies", "configurationDrafts", "governanceAuditEvents"] },
  },
  {
    id: "sod.cmts.governance.sod",
    path: "/cmts/governance/sod",
    title: "Governance SoD",
    module: "Governance",
    feature: "sod",
    permission: "MASTER_DEPENDENCY.READ",
    componentExport: "CmtsSodAdminContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "GOVERNANCE", "primaryActions": ["open-sod-governance"], "requiredSeedEntities": ["sodPolicies", "roles", "permissions"] },
  },
  {
    id: "configuration.cmts.governance.configuration",
    path: "/cmts/governance/configuration",
    title: "Governance Configuration",
    module: "Governance",
    feature: "configuration",
    permission: "CONFIGURATION.APPROVE",
    componentExport: "CmtsConfigurationGovernanceContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "CONFIGURATION", "primaryActions": ["submit-configuration", "approve-configuration", "activate-configuration"], "requiredSeedEntities": ["configurationDrafts", "configurationVersions"] },
  },
  {
    id: "dependencies.cmts.governance.dependencies",
    path: "/cmts/governance/dependencies",
    title: "Governance Dependencies",
    module: "Governance",
    feature: "dependencies",
    permission: "MASTER_DEPENDENCY.READ",
    componentExport: "CmtsMasterDependencyContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "DEPENDENCIES", "primaryActions": ["check-dependencies"], "requiredSeedEntities": ["masterDependencies", "masterData"] },
  },
  {
    id: "calendar.cmts.governance.calendar",
    path: "/cmts/governance/calendar",
    title: "Governance Calendar",
    module: "Governance",
    feature: "calendar",
    permission: "CALENDAR.READ",
    componentExport: "CmtsAdminCalendarSchedulerContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "CALENDAR", "primaryActions": ["update-calendar"], "requiredSeedEntities": ["calendarEvents", "workingCalendars"] },
  },
  {
    id: "calendar-scheduler.cmts.governance.scheduler",
    path: "/cmts/governance/scheduler",
    title: "Governance Scheduler",
    module: "Governance",
    feature: "calendar-scheduler",
    permission: "SLA.READ",
    componentExport: "CmtsAdminCalendarSchedulerContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "CALENDAR_SCHEDULER", "primaryActions": ["schedule-job"], "requiredSeedEntities": ["calendarEvents", "scheduledJobs"] },
  },
  {
    id: "jobs.cmts.governance.jobs",
    path: "/cmts/governance/jobs",
    title: "Governance Jobs",
    module: "Governance",
    feature: "jobs",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsScheduledJobsContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "JOBS", "primaryActions": ["run-scheduled-job"], "requiredSeedEntities": ["scheduledJobs", "operationSpineEvents"] },
  },
  {
    id: "configuration",
    path: "/cmts/admin/configuration-governance",
    title: "Configuration Governance",
    module: "Governance",
    feature: "configuration",
    permission: "CONFIGURATION.APPROVE",
    componentExport: "CmtsConfigurationGovernanceContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "governance",
    menuGroup: "Governance",
    menuOrder: 37,
    menuDescription: "Draft/approve/activate",
    operationSpine: { "domain": "CONFIGURATION", "primaryActions": ["submit-configuration", "approve-configuration", "activate-configuration"], "requiredSeedEntities": ["configurationDrafts", "configurationVersions"] },
  },
  {
    id: "dependencies",
    path: "/cmts/admin/master-dependencies",
    title: "Master Dependencies",
    module: "Governance",
    feature: "dependencies",
    permission: "MASTER_DEPENDENCY.READ",
    componentExport: "CmtsMasterDependencyContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "governance",
    menuGroup: "Governance",
    menuOrder: 38,
    menuDescription: "Impact graph",
    operationSpine: { "domain": "DEPENDENCIES", "primaryActions": ["check-dependencies"], "requiredSeedEntities": ["masterDependencies", "masterData"] },
  },
  {
    id: "evidence.cmts.admin.evidence",
    path: "/cmts/admin/evidence",
    title: "Evidence Admin",
    module: "Evidence",
    feature: "evidence",
    permission: "EVIDENCE_POLICY.MANAGE",
    componentExport: "CmtsAdminEvidenceContainer",
    showInMenu: false,
    requiresTenantScope: true,
    personaLandingFor: ["EVIDENCE_ADMIN"],
    operationSpine: { "domain": "EVIDENCE", "primaryActions": ["upload-evidence"], "requiredSeedEntities": ["evidence", "complaints"] },
  },
  {
    id: "notifications.cmts.admin.notifications",
    path: "/cmts/admin/notifications",
    title: "Email Templates",
    module: "Administration",
    feature: "notifications",
    permission: "PLATFORM_ADMIN.MANAGE",
    componentExport: "CmtsAdminNotificationsContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 32.27,
    menuDescription: "Configure automated notification email templates",
    operationSpine: {"domain": "NOTIFICATIONS", "primaryActions": ["mark-notification-read", "acknowledge-notification"], "requiredSeedEntities": ["notifications", "complaints", "users"]},
  },
  {
    id: "calendar",
    path: "/cmts/admin/calendar",
    title: "Calendar and Scheduler",
    module: "Governance",
    feature: "calendar",
    permission: "CALENDAR.READ",
    componentExport: "CmtsAdminCalendarSchedulerContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "governance",
    menuGroup: "Governance",
    menuOrder: 39,
    menuDescription: "Working calendars",
    operationSpine: { "domain": "CALENDAR", "primaryActions": ["update-calendar"], "requiredSeedEntities": ["calendarEvents", "workingCalendars"] },
  },
  {
    id: "scheduled-jobs",
    path: "/cmts/admin/scheduled-jobs",
    title: "Scheduled Jobs",
    module: "Governance",
    feature: "jobs",
    permission: "SCHEDULED_JOB.READ",
    componentExport: "CmtsScheduledJobsContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "governance",
    menuGroup: "Governance",
    menuOrder: 40,
    menuDescription: "Background operations",
    operationSpine: { "domain": "JOBS", "primaryActions": ["run-scheduled-job"], "requiredSeedEntities": ["scheduledJobs", "operationSpineEvents"] },
  },
  {
    id: "audit.cmts.audit",
    path: "/cmts/audit",
    title: "Audit Logs",
    module: "Audit & Security",
    feature: "audit",
    permission: "AUDIT.READ",
    componentExport: "CmtsAuditLogsContainer",
    showInMenu: false,
    requiresTenantScope: true,
    personaLandingFor: ["AUDITOR"],
    operationSpine: { "domain": "AUDIT", "primaryActions": ["read-audit"], "requiredSeedEntities": ["auditEvents", "securityEvents"] },
  },
  {
    id: "security.cmts.security",
    path: "/cmts/security",
    title: "Security Logs",
    module: "Audit & Security",
    feature: "security",
    permission: "SECURITY.READ",
    componentExport: "CmtsSecurityLogsContainer",
    showInMenu: false,
    requiresTenantScope: true,
    personaLandingFor: ["SECURITY_VIEWER"],
    operationSpine: { "domain": "SECURITY", "primaryActions": ["read-security-log"], "requiredSeedEntities": ["securityEvents", "accessDecisions"] },
  },
  {
    id: "readiness.cmts.operational-readiness",
    path: "/cmts/operational-readiness",
    title: "Operational Readiness",
    module: "Audit & Security",
    feature: "readiness",
    permission: "AUDIT.READ",
    componentExport: "CmtsOperationalReadinessContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "OPERATIONAL_READINESS", "primaryActions": ["open-readiness"], "requiredSeedEntities": ["systemHealthSignals", "operationSpineEvents", "offlineOutbox"] },
  },
  {
    id: "runtime-contract.cmts.runtime.endpoints",
    path: "/cmts/runtime/endpoints",
    title: "Backend Endpoint Coverage",
    module: "Audit & Security",
    feature: "runtime-contract",
    permission: "AUDIT.READ",
    componentExport: "CmtsBackendEndpointCoverageContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "RUNTIME", "primaryActions": ["open-runtime-endpoints"], "requiredSeedEntities": ["systemHealthSignals", "offlineOutbox", "operationSpineEvents"] },
  },
  {
    id: "access.cmts.admin.access",
    path: "/cmts/admin/access",
    title: "Access Governance",
    module: "Audit & Security",
    feature: "access",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsAccessGovernanceContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_ACCESS", "primaryActions": ["open-access-admin"], "requiredSeedEntities": ["users", "roles", "permissions"] },
  },
  {
    id: "references.cmts.admin.references",
    path: "/cmts/admin/references",
    title: "Reference Administration",
    module: "Administration",
    feature: "references",
    permission: "MASTER_DEPENDENCY.READ",
    componentExport: "CmtsReferenceAdminContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_REFERENCE", "primaryActions": ["open-reference-admin"], "requiredSeedEntities": ["masterDataValues", "complaintCategories", "complaintSubcategories"] },
  },
  {
    id: "field-schemas",
    path: "/cmts/admin/field-schemas",
    title: "Field Schemas",
    module: "Administration",
    feature: "field-schemas",
    permission: "MASTER_DEPENDENCY.READ",
    componentExport: "CmtsFieldSchemaAdminContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_FIELD_SCHEMA", "primaryActions": ["open-field-schemas"], "requiredSeedEntities": ["schemaDefinitionMasters", "masterDataValues", "configurationVersions"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 35,
    menuDescription: "Dynamic fields",
  },
  {
    id: "organization",
    path: "/cmts/admin/organization",
    title: "Organization",
    module: "Administration",
    feature: "organization",
    permission: "AUTH.USER_MANAGE",
    componentExport: "CmtsOrganizationAdminContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_ORG", "primaryActions": ["open-organization-admin"], "requiredSeedEntities": ["organizations", "departments", "users"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 33,
    menuDescription: "Users, departments, scope",
  },
  {
    id: "routing",
    path: "/cmts/admin/routing",
    title: "Routing",
    module: "Administration",
    feature: "routing",
    permission: "MASTER_DEPENDENCY.READ",
    componentExport: "CmtsRoutingAdminContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_ROUTING", "primaryActions": ["open-routing-admin"], "requiredSeedEntities": ["routingRuleMasters", "workflowProfiles", "departments"] },
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 34,
    menuDescription: "Routing rules",
  },
  {
    id: "sod",
    path: "/cmts/admin/sod",
    title: "SoD Policies",
    module: "Governance",
    feature: "sod",
    permission: "MASTER_DEPENDENCY.READ",
    componentExport: "CmtsSodAdminContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_SOD", "primaryActions": ["open-sod-admin"], "requiredSeedEntities": ["sodPolicies", "roles", "permissions"] },
    menuGroupId: "governance",
    menuGroup: "Governance",
    menuOrder: 36,
    menuDescription: "Segregation of duties",
  },
  {
    id: "calendar-scheduler.cmts.admin.calendar-scheduler",
    path: "/cmts/admin/calendar-scheduler",
    title: "Calendar and Scheduler",
    module: "Governance",
    feature: "calendar-scheduler",
    permission: "CALENDAR.READ",
    componentExport: "CmtsAdminCalendarSchedulerContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "CALENDAR_SCHEDULER", "primaryActions": ["schedule-job"], "requiredSeedEntities": ["calendarEvents", "scheduledJobs"] },
  },
  {
    id: "system-operations.cmts.admin.system-operations",
    path: "/cmts/admin/system-operations",
    title: "System Operations",
    module: "Audit & Security",
    feature: "system-operations",
    permission: "AUDIT.READ",
    componentExport: "CmtsSystemOperationsContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "SYSTEM_OPERATIONS", "primaryActions": ["run-readiness-check"], "requiredSeedEntities": ["systemOperationRuns", "offlineOutbox"] },
  },
  {
    id: "audit.cmts.admin.audit",
    path: "/cmts/admin/audit",
    title: "Audit Administration",
    module: "Audit & Security",
    feature: "audit",
    permission: "AUDIT.READ",
    componentExport: "CmtsAuditLogsContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "AUDIT", "primaryActions": ["read-audit"], "requiredSeedEntities": ["auditEvents", "securityEvents"] },
  },
  {
    id: "security.cmts.admin.security",
    path: "/cmts/admin/security",
    title: "Security Administration",
    module: "Audit & Security",
    feature: "security",
    permission: "SECURITY.READ",
    componentExport: "CmtsSecurityLogsContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "SECURITY", "primaryActions": ["read-security-log"], "requiredSeedEntities": ["securityEvents", "accessDecisions"] },
  },
  {
    id: "operations.cmts.admin.operations",
    path: "/cmts/admin/operations",
    title: "Operations Administration",
    module: "Audit & Security",
    feature: "operations",
    permission: "AUDIT.READ",
    componentExport: "CmtsOperationalReadinessContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "ADMIN_OPERATIONS", "primaryActions": ["open-operations-admin"], "requiredSeedEntities": ["scheduledJobs", "systemOperationRuns", "operationSpineEvents"] },
  },
  {
    id: "audit",
    path: "/cmts/audit/logs",
    title: "Audit Logs",
    module: "Audit & Security",
    feature: "audit",
    permission: "AUDIT.READ",
    componentExport: "CmtsAuditLogsContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "audit-security",
    menuGroup: "Audit & Security",
    menuOrder: 41,
    menuDescription: "Cross-domain lifecycle audit",
    operationSpine: { "domain": "AUDIT", "primaryActions": ["read-audit"], "requiredSeedEntities": ["auditEvents", "securityEvents"] },
  },
  {
    id: "audit-history",
    path: "/cmts/audit/history",
    title: "Audit History",
    module: "Audit & Security",
    feature: "audit",
    permission: "AUDIT.READ",
    componentExport: "CmtsAuditLogsContainer",
    showInMenu: false,
    requiresTenantScope: true,
    menuGroupId: "audit-security",
    menuGroup: "Audit & Security",
    menuOrder: 42,
    menuDescription: "Snapshots and field changes",
    operationSpine: { "domain": "AUDIT", "primaryActions": ["read-audit"], "requiredSeedEntities": ["auditEvents", "securityEvents"] },
  },
  {
    id: "audit.cmts.audit.export-history",
    path: "/cmts/audit/export-history",
    title: "Audit Export History",
    module: "Audit & Security",
    feature: "audit",
    permission: "AUDIT.READ",
    componentExport: "CmtsAuditLogsContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "AUDIT", "primaryActions": ["read-audit"], "requiredSeedEntities": ["auditEvents", "securityEvents"] },
  },
  {
    id: "security",
    path: "/cmts/security/logs",
    title: "Security Logs",
    module: "Audit & Security",
    feature: "security",
    permission: "SECURITY.READ",
    componentExport: "CmtsSecurityLogsContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "audit-security",
    menuGroup: "Audit & Security",
    menuOrder: 43,
    menuDescription: "RBAC, SoD and scope events",
    operationSpine: { "domain": "SECURITY", "primaryActions": ["read-security-log"], "requiredSeedEntities": ["securityEvents", "accessDecisions"] },
  },
  {
    id: "security.cmts.security.events",
    path: "/cmts/security/events",
    title: "Security Events",
    module: "Audit & Security",
    feature: "security",
    permission: "SECURITY.READ",
    componentExport: "CmtsSecurityLogsContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "SECURITY", "primaryActions": ["read-security-log"], "requiredSeedEntities": ["securityEvents", "accessDecisions"] },
  },
  {
    id: "access-decisions",
    path: "/cmts/security/access-decisions",
    title: "Access Decisions",
    module: "Audit & Security",
    feature: "security",
    permission: "SECURITY.READ",
    componentExport: "CmtsSecurityLogsContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "audit-security",
    menuGroup: "Audit & Security",
    menuOrder: 44,
    menuDescription: "Permission and policy decisions",
    operationSpine: { "domain": "SECURITY", "primaryActions": ["read-security-log"], "requiredSeedEntities": ["securityEvents", "accessDecisions"] },
  },
  {
    id: "security.cmts.security.blocked-actions",
    path: "/cmts/security/blocked-actions",
    title: "Blocked Actions",
    module: "Audit & Security",
    feature: "security",
    permission: "SECURITY.READ",
    componentExport: "CmtsSecurityLogsContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "SECURITY", "primaryActions": ["read-security-log"], "requiredSeedEntities": ["securityEvents", "accessDecisions"] },
  },
  {
    id: "operations.cmts.operations",
    path: "/cmts/operations",
    title: "Operations",
    module: "Audit & Security",
    feature: "operations",
    permission: "AUDIT.READ",
    componentExport: "CmtsOperationalReadinessContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "OPERATIONS", "primaryActions": ["open-operations"], "requiredSeedEntities": ["operationSpineEvents", "systemOperationRuns", "systemOperationFailures"] },
  },
  {
    id: "readiness",
    path: "/cmts/operations/readiness",
    title: "Operation Readiness",
    module: "Audit & Security",
    feature: "readiness",
    permission: "AUDIT.READ",
    componentExport: "CmtsOperationalReadinessContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "OPERATIONS", "primaryActions": ["open-readiness"], "requiredSeedEntities": ["systemHealthSignals", "operationSpineEvents", "offlineOutbox"] },
    menuGroupId: "audit-security",
    menuGroup: "Audit & Security",
    menuOrder: 45,
    menuDescription: "Executable readiness checks",
  },
  {
    id: "endpoint-coverage",
    path: "/cmts/operations/backend-endpoints",
    title: "Backend Endpoint Readiness",
    module: "Audit & Security",
    feature: "runtime-contract",
    permission: "AUDIT.READ",
    componentExport: "CmtsBackendEndpointCoverageContainer",
    showInMenu: true,
    requiresTenantScope: true,
    operationSpine: { "domain": "OPERATIONS", "primaryActions": ["open-backend-endpoints"], "requiredSeedEntities": ["systemHealthSignals", "offlineOutbox", "operationSpineEvents"] },
    menuGroupId: "audit-security",
    menuGroup: "Audit & Security",
    menuOrder: 46,
    menuDescription: "Endpoint probe and proof status",
  },
  {
    id: "system-operations",
    path: "/cmts/operations/system-health",
    title: "System Operations",
    module: "Audit & Security",
    feature: "system-operations",
    permission: "AUDIT.READ",
    componentExport: "CmtsSystemOperationsContainer",
    showInMenu: true,
    requiresTenantScope: true,
    menuGroupId: "audit-security",
    menuGroup: "Audit & Security",
    menuOrder: 47,
    menuDescription: "Runs, failures, outbox and replay",
    operationSpine: { "domain": "SYSTEM_OPERATIONS", "primaryActions": ["run-readiness-check"], "requiredSeedEntities": ["systemOperationRuns", "offlineOutbox"] },
  },
  {
    id: "system-operations.cmts.operations.system-jobs",
    path: "/cmts/operations/system-jobs",
    title: "System Jobs",
    module: "Audit & Security",
    feature: "system-operations",
    permission: "AUDIT.READ",
    componentExport: "CmtsSystemOperationsContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "SYSTEM_OPERATIONS", "primaryActions": ["run-readiness-check"], "requiredSeedEntities": ["systemOperationRuns", "offlineOutbox"] },
  },
  {
    id: "system-operations.cmts.operations.offline-sync",
    path: "/cmts/operations/offline-sync",
    title: "Offline Sync",
    module: "Audit & Security",
    feature: "system-operations",
    permission: "AUDIT.READ",
    componentExport: "CmtsSystemOperationsContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "SYSTEM_OPERATIONS", "primaryActions": ["run-readiness-check"], "requiredSeedEntities": ["systemOperationRuns", "offlineOutbox"] },
  },
  {
    id: "system-operations.cmts.operations.outbox",
    path: "/cmts/operations/outbox",
    title: "Offline Outbox",
    module: "Audit & Security",
    feature: "system-operations",
    permission: "AUDIT.READ",
    componentExport: "CmtsSystemOperationsContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "SYSTEM_OPERATIONS", "primaryActions": ["run-readiness-check"], "requiredSeedEntities": ["systemOperationRuns", "offlineOutbox"] },
  },
  {
    id: "system-operations.cmts.operations.failure-center",
    path: "/cmts/operations/failure-center",
    title: "Failure Center",
    module: "Audit & Security",
    feature: "system-operations",
    permission: "AUDIT.READ",
    componentExport: "CmtsSystemOperationsContainer",
    showInMenu: false,
    requiresTenantScope: true,
    operationSpine: { "domain": "SYSTEM_OPERATIONS", "primaryActions": ["run-readiness-check"], "requiredSeedEntities": ["systemOperationRuns", "offlineOutbox"] },
  },

  {
    id: "admin.identity.tenants",
    path: "/cmts/admin/tenants",
    title: "Tenants",
    module: "Administration",
    feature: "identity-tenants",
    permission: "TENANT.READ",
    componentExport: "CmtsTenantContainer",
    showInMenu: false,
    requiresTenantScope: true,
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 41,
    menuDescription: "Tenant identity administration",
    operationSpine: { "domain": "ADMIN_IDENTITY", "primaryActions": ["create-tenant", "update-tenant", "deactivate-tenant"], "requiredSeedEntities": ["tenants", "auditLogs"] },
  },
  {
    id: "admin.identity.organization-units",
    path: "/cmts/admin/organization-units",
    title: "Organization Units",
    module: "Administration",
    feature: "identity-organization-units",
    permission: "ORG_UNIT.READ",
    componentExport: "CmtsOrganizationUnitContainer",
    showInMenu: false,
    requiresTenantScope: true,
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 42,
    menuDescription: "Organization unit identity administration",
    operationSpine: { "domain": "ADMIN_IDENTITY", "primaryActions": ["create-org-unit", "update-org-unit", "deactivate-org-unit"], "requiredSeedEntities": ["organizationUnits", "auditLogs"] },
  },
  {
    id: "admin.identity.users",
    path: "/cmts/admin/users",
    title: "Users",
    module: "Administration",
    feature: "identity-users",
    permission: "USER.READ",
    componentExport: "CmtsUsersContainer",
    showInMenu: false,
    requiresTenantScope: true,
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 43,
    menuDescription: "User creation, update, role assignment and org scope",
    operationSpine: { "domain": "ADMIN_IDENTITY", "primaryActions": ["create-user", "update-user", "assign-role", "assign-org-scope"], "requiredSeedEntities": ["users", "roles", "organizationUnits", "auditLogs"] },
  },
  {
    id: "admin.identity.roles",
    path: "/cmts/admin/roles",
    title: "Roles",
    module: "Administration",
    feature: "identity-roles",
    permission: "ROLE.READ",
    componentExport: "CmtsRolesContainer",
    showInMenu: false,
    requiresTenantScope: true,
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 44,
    menuDescription: "Role lifecycle and role-permission assignment",
    operationSpine: { "domain": "ADMIN_IDENTITY", "primaryActions": ["create-role", "update-role", "assign-permission"], "requiredSeedEntities": ["roles", "permissions", "auditLogs"] },
  },
  {
    id: "admin.identity.permissions",
    path: "/cmts/admin/permissions",
    title: "Permissions",
    module: "Administration",
    feature: "identity-permissions",
    permission: "PERMISSION.READ",
    componentExport: "CmtsPermissionsContainer",
    showInMenu: false,
    requiresTenantScope: true,
    menuGroupId: "administration",
    menuGroup: "Administration",
    menuOrder: 45,
    menuDescription: "Permission catalogue lifecycle",
    operationSpine: { "domain": "ADMIN_IDENTITY", "primaryActions": ["create-permission", "update-permission", "deactivate-permission"], "requiredSeedEntities": ["permissions", "auditLogs"] },
  },
];

export type CmtsRoutePath = string;
